/**
 * API Core - Base configuration and helpers for FleetManager API
 * @module api/core
 */

// =============================================================================
// CONFIGURATION
// =============================================================================

// API base URLs — always relative so they work behind any reverse proxy (Nginx, dev proxy, etc.)
// Override with REACT_APP_API_URL only if the API is on a different origin (rare)
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

export const MOBILE_API_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api/v1', '/api/mobile/v1')
  : '/api/mobile/v1';

// UI API base (sidebar orchestrator, etc.)
export const UI_API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api/v1', '/api/ui/v1')
  : '/api/ui/v1';

// =============================================================================
// AUTH HELPERS
// =============================================================================

/**
 * Get auth headers from AuthContext (set by AuthContext on mount)
 * @returns {Object} Headers object with Authorization and X-Tenant-Subdomain
 */
export function getAuthHeaders() {
  const headers = {};

  // Get token from window (set by AuthContext)
  if (typeof window !== 'undefined' && window.__getAuthToken) {
    const token = window.__getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Get subdomain for tenant identification
  if (typeof window !== 'undefined' && window.__getAuthSubdomain) {
    const subdomain = window.__getAuthSubdomain();
    if (subdomain) {
      headers['X-Tenant-Subdomain'] = subdomain;
    }
  }

  // Group / multi-org: active organization chosen in the header switcher.
  // Absent or 'all' = every accessible organization (the backend decides the scope).
  const activeOrg = getActiveOrg();
  if (activeOrg) {
    headers['X-Active-Org'] = activeOrg;
  }

  return headers;
}

export const ACTIVE_ORG_KEY = 'fm_active_org';

/**
 * Active organization code for Group tenants (null = all accessible organizations).
 * @returns {string|null}
 */
export function getActiveOrg() {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(ACTIVE_ORG_KEY);
  return v && v !== 'all' ? v : null;
}

/**
 * Get mobile auth token
 * @returns {string|null} Mobile access token
 */
export function getMobileToken() {
  return localStorage.getItem('fm_mobile_access_token');
}

// =============================================================================
// API REQUEST HELPERS
// =============================================================================

// Single-flight refresh: concurrent 401s share one /auth/refresh call instead of each
// firing their own (which would race and cause auth-failure storms).
let _refreshInFlight = null;
function attemptSilentRefresh() {
  if (_refreshInFlight) return _refreshInFlight;
  const refresher = typeof window !== 'undefined' ? window.__refreshAuth : null;
  if (!refresher) return Promise.resolve(false);
  _refreshInFlight = Promise.resolve()
    .then(() => refresher())
    .catch(() => false)
    .finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
}

/**
 * Generic API helper with authentication
 * @param {string} endpoint - API endpoint (e.g., '/vehicle')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} API response
 */
export async function apiRequest(endpoint, options = {}) {
  try {
    const authHeaders = getAuthHeaders();

    // Ensure POST/PUT/PATCH/DELETE always have a body (Fastify gateway rejects empty JSON bodies)
    const fetchOptions = { ...options };
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes((fetchOptions.method || 'GET').toUpperCase()) && !fetchOptions.body) {
      fetchOptions.body = '{}';
    }

    // Skip Content-Type for FormData (browser sets multipart boundary automatically)
    const isFormData = fetchOptions.body instanceof FormData;
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...authHeaders,
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
      ...fetchOptions,
    });

    // 401: try silent refresh once, then retry. Skip for /auth/* (would loop) and
    // if already retried. 403 is authorization (not expiry) — never retry.
    if (response.status === 401 && !options._retriedAfterRefresh && !endpoint.startsWith('/auth/')) {
      const refreshed = await attemptSilentRefresh();
      if (refreshed) {
        return apiRequest(endpoint, { ...options, _retriedAfterRefresh: true });
      }
    }

    if (response.status === 401 || response.status === 403) {
      let data;
      try { data = await response.json(); } catch { data = {}; }
      const msg = (typeof data.error === 'string' && data.error) || data.error?.message
        || (response.status === 403 ? 'Access denied' : 'Session expired');
      // 403 de refus MÉTIER (rôle lecture seule, rôle insuffisant) : ce n'est pas
      // une session invalide — on remonte le message sans déclencher la déconnexion
      // (AuthContext écoute 'auth-failure'). Déconnecter un gestionnaire parce
      // qu'il a cliqué sur une action réservée serait un faux positif brutal.
      // ROLE_NOT_ALLOWED (garde d'écriture conducteur, 12/09) et ROLE_READ_FORBIDDEN
      // (garde de lecture, 13/09) : mesuré dans Chrome le 2026-09-13, un conducteur
      // était DÉCONNECTÉ au boot parce qu'un widget lisait le parc. Un refus de rôle
      // n'est jamais une session morte.
      // Renversé le 2026-09-13 (deuxième mesure du jour : un admin qui PRÉVISUALISE le
      // profil conducteur prenait un 403 NOT_DRIVER du widget pool et était déconnecté) :
      // la liste blanche des « 403 métier » ne tenait pas, chaque nouveau garde la
      // trouait. Désormais la DÉCONNEXION est l'exception : seuls un 401, ou un 403
      // portant un code d'AUTHENTIFICATION (jeton, tenant, compte), déclenchent
      // 'auth-failure'. Tout autre 403 est un refus métier, remonté avec son code.
      const AUTH_403 = ['INVALID_TOKEN', 'NO_TOKEN', 'NO_USER', 'AUTH_ERROR', 'TENANT_MISMATCH', 'TENANT_NOT_FOUND', 'ACCOUNT_OFFBOARDED'];
      const code403 = data.code || data.error?.code;
      const isAuthFailure = response.status === 401 || AUTH_403.includes(code403);
      if (!isAuthFailure) {
        const err = new Error(msg);
        err.code = code403 || 'FORBIDDEN';
        err.status = 403;
        err.data = data.data || null;
        throw err;
      }
      window.dispatchEvent(new CustomEvent('auth-failure', { detail: { status: response.status, error: msg, code: code403 } }));
      throw new Error(msg);
    }

    const data = await response.json();

    if (!data.ok && response.status >= 400) {
      // On conserve le code d'erreur métier : l'appelant en a besoin pour
      // distinguer un refus qu'il sait traiter (confirmation, rechargement)
      // d'une panne. Sans lui, tout se ressemble côté UI.
      // Deux formes d'erreur coexistent : `{ error: { message, code } }` (routes
      // historiques) et `{ error: 'phrase', code, data: { next_actions } }` (module
      // pool, ADR-010). Sans la seconde, le message français devenait « API request
      // failed » et les `next_actions` étaient perdues avant tout composant
      // (mesuré 2026-09-13).
      const flatMessage = typeof data.error === 'string' ? data.error : null;
      const err = new Error(data.error?.message || flatMessage || 'API request failed');
      const code = data.error?.code || data.code;
      if (code) err.code = code;
      err.status = response.status;
      err.details = data.error || null;
      err.data = data.data || null;
      if (data.data?.next_actions) err.next_actions = data.data.next_actions;
      throw err;
    }

    // Check for auto-resolved work items and emit event for toast notification
    if (data._auto_resolved && Array.isArray(data._auto_resolved) && data._auto_resolved.length > 0) {
      window.dispatchEvent(new CustomEvent('work-items-resolved', {
        detail: data._auto_resolved
      }));
    }

    return data;
  } catch (error) {
    // Downgrade expected/optional endpoint failures to warn
    const isOptional = ['/tenant/settings', '/monitor/health', '/pipeline/'].some(p => endpoint.includes(p));
    if (isOptional) {
      console.warn(`API Warning [${endpoint}]:`, error.message);
    } else {
      console.error(`API Error [${endpoint}]:`, error);
    }
    throw error;
  }
}

/**
 * Mobile API request helper with auto-refresh
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} API response
 */
export async function mobileApiRequest(endpoint, options = {}) {
  const token = getMobileToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${MOBILE_API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  // Handle 401 - could trigger refresh here
  if (response.status === 401) {
    // Note: refresh logic is in mobileAPI module
    throw new Error('Session expired');
  }

  return data;
}

/**
 * UI API request helper (sidebar orchestrator, etc.)
 * @param {string} endpoint - API endpoint (e.g., '/sidebar/resolve')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} API response
 */
export async function uiApiRequest(endpoint, options = {}) {
  try {
    const authHeaders = getAuthHeaders();

    const response = await fetch(`${UI_API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    });

    if (response.status === 401) {
      const data = await response.json();
      throw new Error(data.error || 'Session expired');
    }

    const data = await response.json();

    if (!data.ok && response.status >= 400) {
      throw new Error(data.error?.message || data.error || 'UI API request failed');
    }

    return data;
  } catch (error) {
    console.error(`UI API Error [${endpoint}]:`, error);
    throw error;
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Helper to get the correct URL for a document
 * Handles both file_url (preferred) and storage_key/file_path fallbacks
 * @param {Object} doc - Document object with file_url, storage_key, or file_path
 * @returns {string|null} The URL to access the document
 */
export function getDocumentUrl(doc) {
  if (!doc) return null;

  // Prefer file_url if available (already formatted correctly)
  if (doc.file_url) return doc.file_url;

  // Use storage_key with /uploads/ prefix for local storage
  if (doc.storage_key) return `/uploads/${doc.storage_key}`;

  // Fall back to file_path - if it starts with /uploads or http, use as is
  if (doc.file_path) {
    if (doc.file_path.startsWith('/uploads/') || doc.file_path.startsWith('http')) {
      return doc.file_path;
    }
    // Otherwise, prepend /uploads/
    return `/uploads/${doc.file_path}`;
  }

  return null;
}

/**
 * Build query string from params object
 * @param {Object} params - Query parameters
 * @returns {string} Query string (with leading ?)
 */
export function buildQueryString(params) {
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : '';
}
